import React, { CSSProperties, PropsWithChildren, useCallback } from 'react';
import { Draggable } from './Draggable';
import { Droppable } from './Droppable';
import styles from '@/features/Explorer/Sortable.module.scss';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from "@dnd-kit/sortable";
import useExplorerItems from "@/common/hooks/useExplorerItems.ts";
import { ExplorerItemType } from "@/enum/explorerItemType.ts";
import clsx from "clsx";
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys.ts";
import { FlexBox } from "@/common/components/Box/FlexBox.tsx";
import useDataGridApi from "@/common/hooks/useDataGridApi.ts";
import { useSearchParamState } from "@/common/hooks/nuqs/useSearchParamState.ts";
import { EXPLORER_ROOT } from "@/features/Explorer/Explorer.tsx";
import useCurrentContextMenu from "@/common/hooks/useCurrentContextMenu.ts";
import { ContextMenuTypes } from "@/enum/contextMenuTypes.ts";
import { gridRowSelectionManagerSelector } from "@mui/x-data-grid-pro";

type SortablePropsType = {
  id: string,
  disabled?: boolean,
} & PropsWithChildren;

export function Sortable(props: SortablePropsType) {
  const { id, children } = props;
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const [, setCurrentContextMenu] = useCurrentContextMenu();
  const [explorerItems] = useExplorerItems();
  const [playlistId] = useSearchParamState(URLSearchParamKeys.PLAYLIST, { defaultValue: EXPLORER_ROOT })
  const [dataGridApi] = useDataGridApi();
  const rowSelectionManager = gridRowSelectionManagerSelector(dataGridApi!);

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: transition,
    opacity: isDragging ? 0 : 1,
    position: 'relative',
  } as CSSProperties;

  // Bestaande context menu handlers hier...
  const handleOpenPlaylistContextMenu = useCallback((mouseEvent: React.MouseEvent, playlistId: string, explorerItemId: string) => {
    setCurrentContextMenu({
      type: ContextMenuTypes.PLAYLIST,
      data: {
        playlistId,
        explorerItemId,
        mouseEvent,
      }
    });
  }, [setCurrentContextMenu]);

  const handleOpenTrackContextMenu = useCallback((mouseEvent: React.MouseEvent, explorerItemId: string) => {
    setCurrentContextMenu({
      type: ContextMenuTypes.TRACK,
      data: {
        explorerItem: explorerItems[explorerItemId],
        mouseEvent,
      }
    });
  }, [
    explorerItems,
    setCurrentContextMenu
  ]);

  const handleExplorerItemContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const selectedRows = dataGridApi?.current?.getSelectedRows()
    const selectedRowsCount = selectedRows?.size || 0
    const target = event.currentTarget as HTMLDivElement
    const id = target.dataset.id as string;
    const type = target.dataset.type;
    const explorerItem = explorerItems[id]
    if (!explorerItem?.id) {
      console.log('Warning: currentExplorerItem is not defined @ Sortable.handleExplorerItemContextMenu')
      return
    }

    if (selectedRowsCount > 1) {
      setCurrentContextMenu({
        type: ContextMenuTypes.EXPLORER_ITEMS,
        data: {
          mouseEvent: event,
          explorerItem,
        }
      });

      return;
    }

    if (type === ExplorerItemType.PLAYLIST) {
      const playlistId = target.dataset.playlistId as string;
      handleOpenPlaylistContextMenu(event, playlistId, id);
    }

    if (type === ExplorerItemType.TRACK) {
      handleOpenTrackContextMenu(event, id);
    }
  }

  if (explorerItems[id]?.type === ExplorerItemType.TRACK) {
    return (
      <FlexBox
        component="div"
        onContextMenu={handleExplorerItemContextMenu}
        data-id={id}
        data-type={explorerItems[id]?.type}
        data-name={explorerItems[id]?.name}
        ref={setNodeRef}
        style={style}
        className={
          clsx({
            [styles.Sortable]: true,
            ['sortable']: true,
            [styles.rowIsSelected]: dataGridApi?.current?.getSelectedRows().has(id),
            [`playlist-${playlistId}-selectable-row`]: true,
          })
        }
      >
        <Draggable id={id}>
          <div className={
            clsx({
              [styles.draggable]: true,
            })
          }>
            {children}
          </div>
        </Draggable>
      </FlexBox>
    );
  }

  // @note: For now, default return is a PLAYLIST explorer item
  return dataGridApi?.current && (
    <FlexBox
      onContextMenu={handleExplorerItemContextMenu}
      data-id={id}
      data-type={explorerItems[id]?.type}
      data-name={explorerItems[id]?.name}
      ref={setNodeRef}
      style={style}
      className={
        clsx({
          ['sortable']: true,
          [`playlist-${playlistId}-selectable-row`]: true,
          [styles.Sortable]: true,
          [styles.rowIsSelected]: rowSelectionManager.has(id),
        })
      }
    >
      <Droppable id={id}>
        <Draggable id={id}>
          <div className={
            clsx({
              [styles.draggable]: true,
            })
          }>
            {children}
          </div>
        </Draggable>
      </Droppable>
    </FlexBox>
  );
}
